import { Link, useNavigate, useParams } from 'react-router-dom'
import { PageChrome, ROUTES, useIsDesktop } from '@/components/gg/chrome'
import { useRecordFinds, useDismissFind, findHeadline } from '@/hooks/useRecords'
import { FindTag, DocLink, filedLine, sortFinds } from '@/components/records/FindBits'
import { dayOf, longDay, useQueueBuild } from '@/components/alerts/ui'
import { SeedCost } from '@/components/gg/SeedChip'
import '@/styles/alerts.css'

/**
 * One record find (/app/records/:id): the find as the desktop pane shows it
 * (records.html v1.1): type and place, the document and its date, the plain
 * facts, the next date, and Dismiss or Build · 12. On a computer the next find
 * opens after either one; the phone shows what happened to it. Marked Public record; never presented as a scoop.
 */
export function RecordFindPage() {
  const { id } = useParams()
  const nav = useNavigate()
  const desk = useIsDesktop()
  const { data: finds, isLoading } = useRecordFinds()
  const dismiss = useDismissFind()
  const qb = useQueueBuild(findHeadline, [['record-finds'], ['records-state']])
  const f = finds?.find((x) => x.id === id)
  const waiting = sortFinds((finds ?? []).filter((x) => x.status === 'new'))
  const earlier = f?.follows_up ? finds?.find((x) => x.id === f.follows_up) : null

  const chrome = <PageChrome title="Record find" navKey="records" back={{ to: ROUTES.records, label: 'Record finds' }} />
  if (!f) return <>{chrome}<div className="gg gga gga-one"><p className="dek">{isLoading ? 'Loading' : 'That find isn’t here.'}</p></div></>

  const next = () => {
    if (!desk) return
    const i = waiting.findIndex((x) => x.id === f.id)
    const n = waiting[i + 1] ?? waiting[i - 1]
    if (n && n.id !== f.id) nav(ROUTES.recordFind(n.id), { replace: true })
  }
  const onDismiss = () => { dismiss.mutate(f.id); qb.toast.show(<>Dismissed. <b>It stays under Earlier.</b></>); next() }
  const onBuild = () => qb.build(f.id, () => { qb.toast.show(<>Building. <b>Emma is writing it; it lands in your Library, marked Public record.</b></>); next() })

  return (
    <>
      {chrome}
      <div className="gg gga gga-one">
        <div className="eb">Public record{f.decided ? ' · update' : ''}</div>
        <FindTag f={f} />
        <h1>{f.title}</h1>
        <div className="by"><DocLink f={f} className="src" /><span>{filedLine(f)}</span></div>
        {f.facts.map((p, i) => <p className="dek" key={i}>{p}</p>)}
        {f.next_date && <div className="age"><b style={{ color: 'var(--amber)' }}>{dayOf(f.next_date)}</b>&nbsp;{f.next_what}</div>}
        {earlier && <div className="banks" style={{ gridTemplateColumns: '1fr' }}><span>Follows <Link to={ROUTES.recordFind(earlier.id)}>{earlier.title}</Link></span></div>}
        {f.status === 'new'
          ? <div className="banks"><span>Checked {longDay(f.doc_checked)}, from your records budget</span><span>Build writes it like any story, marked Public record</span></div>
          : <div className="done">{f.status === 'built' ? <>Built. <b>It’s in your Library, marked Public record.</b></> : 'Dismissed. It stays under Earlier in Record finds.'}</div>}
        <div className="gga-bar">
          {f.status === 'new' ? (
            <>
              <button type="button" className="gga-btn" onClick={onDismiss}>Dismiss</button>
              <button type="button" className="gga-btn gga-btn--b" onClick={onBuild}>Build<SeedCost n={qb.cost} /></button>
            </>
          ) : (
            <>
              <Link className="gga-btn" to={ROUTES.records}>Record finds</Link>
              {f.status === 'built' && f.story_id && <Link className="gga-btn gga-btn--b" to={ROUTES.story(f.story_id)}>Open the story</Link>}
            </>
          )}
        </div>
      </div>
      {qb.confirm}
      {qb.toast.node}
    </>
  )
}
